class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

/* WroeTrends product-card colour swatches - show every colour */
(() => {
  if (window.__wtAllCardSwatchesLoaded) return;
  window.__wtAllCardSwatchesLoaded = true;

  const CARD_SELECTOR = '.product-card-wrapper';
  const SWATCH_CLASS = 'wt-card-swatches';
  const STYLE_ID = 'wt-card-swatches-style';
  const searchCache = new Map();

  const colourMap = {
    black: '#171717', white: '#f7f7f3', 'off white': '#eee9dd', cream: '#efe4cf',
    beige: '#d8c5a5', sand: '#d7c3a2', nude: '#d6bca6', stone: '#b9b1a5',
    taupe: '#a9967c', grey: '#b8bcc2', gray: '#b8bcc2', charcoal: '#55585d',
    silver: '#b9bec5', green: '#486a50', spruce: '#345443', 'spruce green': '#345443',
    olive: '#69714d', khaki: '#9d916c', teal: '#2e7473', blue: '#568fc4',
    'ocean blue': '#467da6', navy: '#24344f', 'royal blue': '#315eaa', red: '#b42b32',
    maroon: '#6e2835', burgundy: '#722f3b', brown: '#78523b', chocolate: '#5f4030',
    espresso: '#46352f', 'faded brown': '#8a6b57', purple: '#76558f', pink: '#df7892',
    'hot pink': '#df4d86', orange: '#d67b42', yellow: '#d4b847', gold: '#b6923d', neon: '#b7df45'
  };

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .${SWATCH_CLASS} {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: .7rem;
        margin: .8rem 0 .45rem;
        min-height: 2rem;
        position: relative;
        z-index: 3;
      }
      .${SWATCH_CLASS} a.${SWATCH_CLASS}__item {
        display: block !important;
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        border: .1rem solid rgba(var(--color-foreground), .22);
        background: var(--wt-swatch, #d2d2d2);
        text-decoration: none;
        flex: 0 0 auto;
        position: relative;
        z-index: 4;
      }
      .${SWATCH_CLASS} a.${SWATCH_CLASS}__item.is-active {
        outline: .1rem solid rgba(var(--color-foreground), .85);
        outline-offset: .2rem;
      }
      .${SWATCH_CLASS} a.${SWATCH_CLASS}__item:focus-visible {
        outline: .2rem solid rgba(var(--color-foreground), 1);
        outline-offset: .2rem;
      }
      @media screen and (max-width: 749px) {
        .${SWATCH_CLASS} { gap: .6rem; margin-top: .7rem; }
        .${SWATCH_CLASS} a.${SWATCH_CLASS}__item { width: 1.7rem; height: 1.7rem; }
      }
    `;
    document.head.appendChild(style);
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function singleColour(value) {
    const n = normalize(value);
    if (colourMap[n]) return colourMap[n];
    const key = Object.keys(colourMap)
      .sort((a, b) => b.length - a.length)
      .find((item) => n.includes(item));
    return key ? colourMap[key] : null;
  }

  function looksLikeColour(value) {
    const n = normalize(value);
    if (!n || n.length > 32) return false;
    if (singleColour(n)) return true;
    return n.split(/\s*(?:\/|&|\+)\s*/).some((part) => singleColour(part));
  }

  function swatchBackground(value) {
    const parts = String(value || '').split(/\s*(?:\/|&|\+)\s*/).filter(Boolean);
    const colours = parts.map(singleColour).filter(Boolean);
    if (colours.length >= 2) {
      return `linear-gradient(135deg, ${colours[0]} 0 49%, ${colours[1]} 51% 100%)`;
    }
    return singleColour(value) || '#d2d2d2';
  }

  function splitTitle(title) {
    const clean = String(title || '').replace(/\s+/g, ' ').trim();
    const match = clean.match(/^(.*)\s+-\s+(.+)$/);
    if (!match) return { base: clean, colour: null };
    const colour = match[2].trim();
    if (!looksLikeColour(colour)) return { base: clean, colour: null };
    return { base: match[1].trim(), colour };
  }

  function preservePreview(href) {
    try {
      const url = new URL(href, window.location.origin);
      const current = new URL(window.location.href);
      const previewThemeId = current.searchParams.get('preview_theme_id');
      const previewBar = current.searchParams.get('pb');
      if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
      if (previewBar !== null) url.searchParams.set('pb', previewBar);
      return url.href;
    } catch (error) {
      return href;
    }
  }

  function cardData(card) {
    const titleLink = card.querySelector('.card__heading a[href*="/products/"]');
    if (!titleLink) return null;
    const title = titleLink.textContent.replace(/\s+/g, ' ').trim();
    const parsed = splitTitle(title);
    return {
      card,
      href: titleLink.href,
      title,
      base: parsed.base,
      baseKey: normalize(parsed.base),
      colour: parsed.colour
    };
  }

  function uniqueItems(items) {
    const seen = new Set();
    return items.filter((item) => {
      const key = normalize(item.colour);
      if (!item.colour || !item.href || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function createSwatches(items, activeColour) {
    const unique = uniqueItems(items);
    if (unique.length <= 1) return null;

    const wrap = document.createElement('div');
    wrap.className = SWATCH_CLASS;
    wrap.setAttribute('aria-label', 'Available colours');

    unique.forEach((item, index) => {
      const link = document.createElement('a');
      link.className = `${SWATCH_CLASS}__item`;
      if ((activeColour && normalize(item.colour) === normalize(activeColour)) || (!activeColour && index === 0)) {
        link.classList.add('is-active');
      }
      link.href = preservePreview(item.href);
      link.title = item.colour;
      link.setAttribute('aria-label', item.colour);
      link.style.setProperty('--wt-swatch', swatchBackground(item.colour));

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = item.colour;
      link.appendChild(label);
      wrap.appendChild(link);
    });

    return wrap;
  }

  function insertSwatches(data, swatches) {
    if (!swatches || data.card.querySelector(`.${SWATCH_CLASS}`)) return false;
    const info = data.card.querySelector('.card-information');
    if (!info) return false;
    const price = info.querySelector('.price');
    if (price) info.insertBefore(swatches, price);
    else info.appendChild(swatches);
    data.card.dataset.wtSwatches = 'true';
    return true;
  }

  async function variantSwatches(data) {
    try {
      const url = new URL(data.href, window.location.origin);
      const response = await fetch(`${url.pathname.replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return false;

      const product = await response.json();
      const optionNames = Array.isArray(product.options)
        ? product.options.map((option) => typeof option === 'string' ? option : option?.name)
        : [];
      const colourIndex = optionNames.findIndex((name) => /^(colou?r)$/i.test(String(name || '').trim()));
      if (colourIndex < 0 || !Array.isArray(product.variants)) return false;

      const byColour = new Map();
      product.variants.forEach((variant) => {
        const colour = variant.options?.[colourIndex] ?? variant[`option${colourIndex + 1}`];
        const key = normalize(colour);
        if (!colour || byColour.has(key)) return;
        const variantUrl = new URL(data.href, window.location.origin);
        variantUrl.searchParams.set('variant', variant.id);
        byColour.set(key, { colour, href: variantUrl.href, available: variant.available });
      });

      if (byColour.size <= 1) return false;
      const items = Array.from(byColour.values());
      const currentVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      const activeColour = currentVariant
        ? (currentVariant.options?.[colourIndex] ?? currentVariant[`option${colourIndex + 1}`])
        : items[0]?.colour;
      return insertSwatches(data, createSwatches(items, activeColour));
    } catch (error) {
      return false;
    }
  }

  async function siblingSwatches(data) {
    if (!data.colour) return false;
    try {
      let promise = searchCache.get(data.baseKey);
      if (!promise) {
        const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
        const params = new URLSearchParams();
        params.set('q', data.base);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '10');
        params.set('resources[options][unavailable_products]', 'show');
        params.set('resources[options][fields]', 'title');
        promise = fetch(`${root}search/suggest.json?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        })
          .then((response) => response.ok ? response.json() : null)
          .then((json) => json?.resources?.results?.products || [])
          .catch(() => []);
        searchCache.set(data.baseKey, promise);
      }

      const products = await promise;
      const items = products
        .map((product) => {
          const parsed = splitTitle(product.title);
          return { baseKey: normalize(parsed.base), colour: parsed.colour, href: product.url };
        })
        .filter((item) => item.colour && item.href && item.baseKey === data.baseKey);

      if (!items.some((item) => normalize(item.colour) === normalize(data.colour))) {
        items.push({ colour: data.colour, href: data.href });
      }
      return insertSwatches(data, createSwatches(items, data.colour));
    } catch (error) {
      return false;
    }
  }

  async function processCard(data) {
    if (!data || data.card.dataset.wtSwatches || data.card.dataset.wtSwatchesLoading) return;
    data.card.dataset.wtSwatchesLoading = 'true';
    try {
      if (await variantSwatches(data)) return;
      await siblingSwatches(data);
    } finally {
      delete data.card.dataset.wtSwatchesLoading;
    }
  }

  function processProductCards() {
    injectStyles();
    Array.from(document.querySelectorAll(CARD_SELECTOR))
      .filter((card) => !card.dataset.wtSwatches)
      .map(cardData)
      .filter(Boolean)
      .forEach(processCard);
  }

  function start() {
    processProductCards();
    let timer;
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        return !target?.closest?.(`.${SWATCH_CLASS}`);
      });
      if (!relevant) return;
      clearTimeout(timer);
      timer = setTimeout(processProductCards, 180);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
